/** Evidence, laid out as a grid of labelled facts.
 *
 * The admin panel shows a lot of this: what the gate measured, which metre
 * two accounts disagreed about, what a run cost. The design draws them all
 * the same way — cells of the page's own ground colour on a divider-coloured
 * background, so the single-pixel gaps between them read as hairlines without
 * a border on any cell and without the doubled line two adjacent borders
 * would make.
 *
 * The cells wrap on their own (`auto-fit` over a minimum width), which is
 * what lets one component serve a four-fact issue on a wide screen and the
 * same four facts stacked on a phone. Values are tabular and allowed to break
 * anywhere, because half of them are identifiers with no spaces in them and a
 * digest that overflows its cell would push the whole card sideways.
 */

import type { ReactNode } from 'react';

export interface KeyValueItem {
  key: string;
  value: ReactNode;
}

export interface KeyValueGridProps {
  items: readonly KeyValueItem[];
  /** The narrowest a cell may be before the grid drops to fewer columns. */
  min?: number;
  /** Names the list for anyone who cannot see the layout. */
  label?: string;
  className?: string;
}

export function KeyValueGrid({ items, min = 200, label, className }: KeyValueGridProps) {
  if (items.length === 0) return null;

  return (
    <dl
      aria-label={label}
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: 1,
        margin: 0,
        background: 'var(--color-divider)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {items.map((item) => (
        <div key={item.key} style={{ background: 'var(--color-bg)', padding: '10px 12px' }}>
          <dt
            style={{
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-neutral-500)',
            }}
          >
            {item.key}
          </dt>
          <dd
            className="tabular-nums"
            style={{ fontSize: 14, margin: '2px 0 0', overflowWrap: 'anywhere' }}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default KeyValueGrid;
